import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Activity, Brain, ExternalLink, FileText, Inbox, Loader2, Mail, MessageSquare,
  Minus, Plus, StickyNote, Clock, Zap, Crosshair, X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDayTime } from '@/lib/format';
import {
  buildAgentGraph, layoutAgentGraph, graphBounds, synapsePath,
  type AgentGraphNode, type AgentNodeKind, type CaseDetailInput, type CaseSummaryInput, type Point,
} from '@/lib/agentGraph';

/**
 * Cloud-Agent-Karte — randlose Leinwand statt Liste.
 *
 * Jedes Ticket ist ein Knoten; was der Agent daran getan hat (Überlegungen,
 * Aktionen, Entwürfe) hängt als Satellit daran, interne Konversationen sind
 * eigene Ticket-Knoten mit eigenen Satelliten. Das Layout kommt aus
 * lib/agentGraph (Kraftsimulation mit festem Seed) — bewusst NICHT symmetrisch:
 * man soll ein gewachsenes Netz sehen, kein Organigramm.
 */

const KIND_ICON: Record<AgentNodeKind, React.ElementType> = {
  case: Inbox,
  child: Mail,
  thought: Brain,
  action: Zap,
  draft: FileText,
  note: StickyNote,
  status: Activity,
  reply: MessageSquare,
  waiting: Clock,
  result: Activity,
};

const fmtTime = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : formatDayTime(d);
};

interface Props {
  cases: CaseSummaryInput[];
  details: Record<string, CaseDetailInput | undefined>;
  loadingCaseIds: string[];
  onExpand: (ticketId: string) => void;
}

export const AgentNeuralCanvas: React.FC<Props> = ({ cases, details, loadingCaseIds, onExpand }) => {
  const { t } = useTranslation();
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const positionsRef = useRef<Record<string, Point>>({});
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const dragRef = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null);
  const fittedNodeCount = useRef(0);

  const labels = useMemo(() => ({
    thought: t('agent:flow.node.thought', 'Überlegung'),
    action: t('agent:flow.node.action', 'Aktion'),
    draft: t('agent:flow.node.draft', 'Kundenentwurf'),
    note: t('agent:flow.node.note', 'Interne Notiz'),
    status: t('agent:flow.node.status', 'Status'),
    reply: t('agent:flow.node.replyReceived', 'Antwort erhalten'),
    waiting: t('agent:flow.legend.waiting', 'Wartet'),
    waitingTime: t('agent:flow.node.waitingTime', 'Wiedervorlage'),
    waitingReply: t('agent:flow.node.waitingReply', 'Wartet auf Antwort'),
    internalComm: t('agent:flow.node.internalComm', 'Interne Konversation'),
    internal: t('agent:flow.node.internalTarget', 'Intern'),
  }), [t]);

  const graph = useMemo(
    () => buildAgentGraph(cases, details, labels, fmtTime),
    [cases, details, labels],
  );

  // Layout: bekannte Knoten behalten ihre Position (sonst springt die Karte
  // bei jedem Refresh), neue Knoten wachsen an ihren Hubs heran.
  const positions = useMemo(() => {
    const next = layoutAgentGraph(graph, {
      previous: positionsRef.current,
      iterations: Object.keys(positionsRef.current).length ? 90 : 280,
    });
    positionsRef.current = next;
    return next;
  }, [graph]);

  const bounds = useMemo(() => graphBounds(positions, 170), [positions]);
  const nodeById = useMemo(() => new Map(graph.nodes.map((n) => [n.id, n])), [graph.nodes]);
  const selected = selectedId ? nodeById.get(selectedId) : undefined;

  const fit = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const scale = Math.min(1.3, Math.max(0.3, Math.min(el.clientWidth / bounds.width, el.clientHeight / bounds.height)));
    setView({
      scale,
      x: el.clientWidth / 2 - ((bounds.minX + bounds.maxX) / 2) * scale,
      y: el.clientHeight / 2 - ((bounds.minY + bounds.maxY) / 2) * scale,
    });
  }, [bounds]);

  useEffect(() => {
    if (!graph.nodes.length) return;
    const needsFit = fittedNodeCount.current === 0
      || graph.nodes.length > fittedNodeCount.current * 1.35;
    if (!needsFit) return;
    fittedNodeCount.current = graph.nodes.length;
    fit();
  }, [fit, graph.nodes.length]);

  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const el = viewportRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    setView((v) => {
      const factor = Math.exp(-e.deltaY * 0.0016);
      const scale = Math.min(2.2, Math.max(0.2, v.scale * factor));
      const k = scale / v.scale;
      return { scale, x: px - (px - v.x) * k, y: py - (py - v.y) * k };
    });
  }, []);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.agent-map-node')) return;
    setView((v) => { dragRef.current = { x: e.clientX, y: e.clientY, vx: v.x, vy: v.y }; return v; });
  }, []);

  useEffect(() => {
    const move = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      setView((v) => ({ ...v, x: d.vx + (e.clientX - d.x), y: d.vy + (e.clientY - d.y) }));
    };
    const up = () => { dragRef.current = null; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, []);

  const zoomBy = (factor: number) => setView((v) => {
    const el = viewportRef.current;
    const cx = (el?.clientWidth || 800) / 2;
    const cy = (el?.clientHeight || 600) / 2;
    const scale = Math.min(2.2, Math.max(0.2, v.scale * factor));
    const k = scale / v.scale;
    return { scale, x: cx - (cx - v.x) * k, y: cy - (cy - v.y) * k };
  });

  const activeIds = useMemo(() => {
    const id = hoverId || selectedId;
    if (!id) return null;
    const set = new Set<string>([id]);
    graph.edges.forEach((e) => {
      if (e.from === id) set.add(e.to);
      if (e.to === id) set.add(e.from);
    });
    return set;
  }, [hoverId, selectedId, graph.edges]);

  const handleNodeClick = (node: AgentGraphNode) => {
    setSelectedId(node.id);
    if (node.kind === 'case' && !details[node.caseId]) onExpand(node.caseId);
  };

  const raw = (selected?.raw || {}) as Record<string, any>;
  const rawMeta = (raw.metadata || null) as Record<string, unknown> | null;
  const liveCaseCount = useMemo(
    () => cases.filter((item) => !['done', 'failed'].includes(item.status)).length,
    [cases],
  );

  return (
    <div className="agent-map">
      <div
        className="agent-map-viewport"
        ref={viewportRef}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
      >
        <div
          className="agent-map-plane"
          style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
        >
          <svg className="agent-map-synapses" aria-hidden="true">
            <g>
              {graph.edges.map((edge) => {
                const a = positions[edge.from];
                const b = positions[edge.to];
                if (!a || !b) return null;
                const dim = activeIds && !(activeIds.has(edge.from) && activeIds.has(edge.to));
                return (
                  <path
                    key={edge.id}
                    d={synapsePath(a, b, edge.kind === 'sequence' ? 0.06 : 0.16)}
                    className={cn(
                      'agent-map-synapse',
                      `agent-map-synapse--${edge.kind}`,
                      edge.pending && 'agent-map-synapse--pending',
                      dim && 'agent-map-synapse--dim',
                    )}
                  />
                );
              })}
            </g>
          </svg>

          {graph.nodes.map((node) => {
            const p = positions[node.id];
            if (!p) return null;
            const Icon = KIND_ICON[node.kind];
            const dim = activeIds && !activeIds.has(node.id);
            const loading = node.kind === 'case' && loadingCaseIds.includes(node.caseId);
            const collapsed = node.kind === 'case' && !details[node.caseId];
            return (
              <button
                type="button"
                key={node.id}
                className={cn(
                  'agent-map-node',
                  `agent-map-node--${node.kind}`,
                  node.hub && 'agent-map-node--hub',
                  node.pending && 'agent-map-node--pending',
                  node.running && 'agent-map-node--live',
                  node.failed && 'agent-map-node--failed',
                  selectedId === node.id && 'agent-map-node--selected',
                  dim && 'agent-map-node--dim',
                )}
                style={{ left: p.x, top: p.y }}
                onClick={() => handleNodeClick(node)}
                onMouseEnter={() => setHoverId(node.id)}
                onMouseLeave={() => setHoverId((h) => (h === node.id ? null : h))}
              >
                <span className="agent-map-node-core" aria-hidden="true">
                  {loading ? <Loader2 className="agent-map-spin" /> : <Icon />}
                </span>
                <span className="agent-map-node-label">
                  <span className="agent-map-node-heading">
                    <span className="agent-map-node-title">{node.title}</span>
                    {node.hub && node.status && (
                      <span className="agent-map-node-status"><i />{t(`agent:caseStatus.${node.status}`, node.status)}</span>
                    )}
                  </span>
                  {node.subtitle && <span className="agent-map-node-sub">{node.subtitle}</span>}
                  {(node.time || collapsed) && (
                    <span className="agent-map-node-foot">
                      {node.time}
                      {collapsed && <span className="agent-map-node-expand">{t('agent:map.expand', 'öffnen')}</span>}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="agent-map-hud agent-map-hud--left">
        <span className="agent-map-hud-title">
          <span className="agent-map-hud-mark"><Brain /></span>
          <span>AI Support Staff</span>
        </span>
        <span className="agent-map-hud-sub">
          <i />{liveCaseCount} aktiv · {t('agent:map.caseCount', '{{count}} Fälle', { count: cases.length })}
        </span>
      </div>

      <div className="agent-map-hud agent-map-hud--right">
        <div className="agent-map-legend">
          {([['thought', labels.thought], ['action', labels.action], ['child', labels.internalComm],
            ['draft', labels.draft], ['waiting', labels.waiting]] as Array<[AgentNodeKind, string]>).map(([kind, label]) => {
            const Icon = KIND_ICON[kind];
            return (
              <span key={kind} className={cn('agent-map-legend-item', `agent-map-legend-item--${kind}`)}>
                <Icon />{label}
              </span>
            );
          })}
        </div>
        <div className="agent-map-zoom">
          <button type="button" onClick={() => zoomBy(1 / 1.2)} title={t('agent:flow.zoomOut', 'Verkleinern')}><Minus /></button>
          <span>{Math.round(view.scale * 100)}%</span>
          <button type="button" onClick={() => zoomBy(1.2)} title={t('agent:flow.zoomIn', 'Vergrössern')}><Plus /></button>
          <button type="button" onClick={fit} title={t('agent:map.fit', 'Alles zeigen')}><Crosshair /></button>
        </div>
      </div>

      {selected && (
        <aside className="agent-map-inspector">
          <header>
            <span className="agent-map-inspector-kind">{selected.title}</span>
            {selected.time && <span className="agent-map-inspector-time">{selected.time}</span>}
            <button type="button" onClick={() => setSelectedId(null)} aria-label={t('common:action.close', 'Schliessen')}><X /></button>
          </header>
          <div className="agent-map-inspector-body">
            {selected.subtitle && <p className="agent-map-inspector-lead">{selected.subtitle}</p>}
            {typeof raw.content === 'string' && raw.content.trim() && (
              <p className="agent-map-inspector-text">{raw.content}</p>
            )}
            {typeof raw.summary === 'string' && raw.summary.trim() && (
              <p className="agent-map-inspector-text">{raw.summary}</p>
            )}
            {selected.ticketId && (
              <Link className="agent-map-inspector-link" to={`/tickets/${selected.ticketId}`}>
                <ExternalLink />{t('agent:map.openTicket', 'Ticket öffnen')}
              </Link>
            )}
            {rawMeta && Object.keys(rawMeta).length > 0 && (
              <dl className="agent-map-inspector-meta">
                {Object.entries(rawMeta)
                  .filter(([, v]) => v !== null && v !== undefined && v !== '')
                  .map(([k, v]) => (
                    <React.Fragment key={k}>
                      <dt>{k}</dt>
                      <dd>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
                    </React.Fragment>
                  ))}
              </dl>
            )}
          </div>
        </aside>
      )}
    </div>
  );
};

export default AgentNeuralCanvas;
